/**
 * Shared "open Advasco ticket" configuration and the web-app (Venus) open
 * mechanism.
 *
 * Kramer's Advasco ticket system has no openable URL: a ticket is opened by
 * POSTing its case id to a fixed path. Neptune runs a content script *inside*
 * the Advasco page, so it POSTs the relative `path` over the extension bridge
 * (see neptune's shell/open-ticket.ts). Venus is a standalone web app on a
 * different origin, so it opens the ticket by submitting a hidden, auto-target
 * `<form method="POST" target="_blank">` to the tenant's absolute Advasco base
 * URL (the tenant's `ticketSystemUrl`) + this `path`.
 *
 * Hardcoded on purpose: this is a single-customer feature. If a second customer
 * needs it, add their tenant id to ADVASCO_TENANT_IDS (or move this to
 * per-tenant DB config). The absolute host is NOT hardcoded — it comes from the
 * tenant's ticketSystemUrl so prod/staging differ without a code change.
 */
import { toast } from "sonner";
import { ADVASCO_TENANT_IDS } from "./advascoTenants";

/** Tenant ids that use the Advasco POST-to-open flow. */
export { ADVASCO_TENANT_IDS };

export const ADVASCO_TICKET_OPEN = {
  path: "/sts/openTicket.do",
  param: "ticketId",
} as const;

/**
 * Open an Advasco ticket from a standalone web app (Venus) by POSTing the case
 * id in a new tab. Builds a transient hidden form, submits it with
 * `target="_blank"`, and removes it. A cross-origin form POST is allowed (we
 * cannot read the response, but we do not need to); this mirrors what a
 * server-rendered Advasco link would do.
 *
 * `baseUrl` is the tenant's Advasco origin (e.g. "https://advasco.example.com");
 * a trailing slash is tolerated.
 */
export function submitAdvascoOpenTicketForm(baseUrl: string, caseId: string): void {
  if (typeof document === "undefined") return;
  const action = `${baseUrl.replace(/\/+$/, "")}${ADVASCO_TICKET_OPEN.path}`;
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  form.target = "_blank";
  form.style.display = "none";

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = ADVASCO_TICKET_OPEN.param;
  input.value = caseId;
  form.appendChild(input);

  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    document.body.removeChild(form);
  }
}

/**
 * Web-app (Venus) equivalent of neptune's buildOpenExternalTicket.
 *
 * Returns an `onOpenExternalTicket` callback for `MessageReferences` when the
 * current tenant uses the Advasco POST-to-open flow (hardcoded tenant gate) AND
 * the tenant has a `ticketSystemUrl` configured; returns null otherwise, so
 * references keep their normal link/dialog behaviour for every other tenant.
 *
 * `ticketSystemUrl` is the tenant's absolute Advasco origin (tenant table
 * column, exposed on the Tenant GraphQL type). The caller passes the current
 * tenant's id + ticketSystemUrl (e.g. from Venus's ProfileProvider).
 */
export function buildOpenExternalTicketWeb(
  tenantId: string | null | undefined,
  ticketSystemUrl: string | null | undefined,
): ((caseId: string) => void) | null {
  if (!tenantId || !ADVASCO_TENANT_IDS.has(tenantId) || !ticketSystemUrl) return null;
  return (caseId: string) => {
    try {
      submitAdvascoOpenTicketForm(ticketSystemUrl, caseId);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };
}
